import { useEffect, useState } from "react";
import { Users2, Eye, FileText, TrendingUp, UserCheck, Search, Monitor, Smartphone, Tablet, Globe, Euro, Wallet, PiggyBank, ShoppingBag, ArrowUpRight, ArrowDownRight, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const Card = ({ icon: Icon, label, value, hint }) => (
  <div className="rounded-xl bg-white border border-border p-6" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-stone">{label}</span>
      <Icon className="text-brand" size={20} />
    </div>
    <p className="mt-3 font-display text-4xl text-forest">{value}</p>
    {hint && <p className="text-xs text-stone mt-1">{hint}</p>}
  </div>
);

const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Delta = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-xs text-stone">—</span>;
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-green-600" : "text-red-500"}`}>
      {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{Math.abs(value)}%
    </span>
  );
};

const BizCard = ({ icon: Icon, label, value, delta, color = "text-forest", hint }) => (
  <div className="rounded-xl bg-white border border-border p-5" data-testid={`biz-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-stone">{label}</span>
      <Icon size={18} className="text-brand" />
    </div>
    <p className={`font-display text-3xl ${color}`}>{value}</p>
    <div className="mt-1 flex items-center gap-2">{delta !== undefined && <Delta value={delta} />}{hint && <span className="text-xs text-stone">{hint}</span>}</div>
  </div>
);

const PERIODS = [{ d: 7, l: "7 jours" }, { d: 30, l: "30 jours" }, { d: 90, l: "90 jours" }, { d: 365, l: "12 mois" }];

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
const DEVICE_LABEL = { desktop: "Ordinateur", mobile: "Mobile", tablet: "Tablette" };

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [biz, setBiz] = useState(null);
  const [days, setDays] = useState(30);
  const [loadingBiz, setLoadingBiz] = useState(true);

  useEffect(() => { api.get("/analytics/summary").then((r) => setS(r.data)); }, []);
  useEffect(() => {
    setLoadingBiz(true);
    api.get(`/analytics/business?days=${days}`).then((r) => setBiz(r.data)).finally(() => setLoadingBiz(false));
  }, [days]);

  if (!s) return <p className="text-stone">Chargement…</p>;

  const totalSources = (s.sources || []).reduce((a, b) => a + b.count, 0) || 1;
  const totalDevices = (s.devices || []).reduce((a, b) => a + b.count, 0) || 1;
  const c = biz?.current || {};

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-1">Tableau de bord</h1>
      <p className="text-stone mb-8">Vue d'ensemble de votre activité, votre trafic et votre référencement</p>

      {/* ============ BUSINESS ANALYTICS ============ */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl text-forest flex items-center gap-2"><PiggyBank size={22} className="text-brand" /> Performance commerciale</h2>
        <div className="inline-flex rounded-full bg-white border border-border p-1" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button key={p.d} onClick={() => setDays(p.d)} data-testid={`period-${p.d}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${days === p.d ? "bg-brand text-white" : "text-stone hover:text-forest"}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {biz?.products_missing_cost > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 mb-4 flex items-center gap-2 text-sm text-amber-800" data-testid="missing-cost-warning">
          <AlertTriangle size={16} className="shrink-0" />
          {biz.products_missing_cost} produit(s) actif(s) sans prix d'achat renseigné — les marges peuvent être surévaluées.
          <Link to="/admin/produits" className="underline font-medium ml-auto shrink-0">Renseigner</Link>
        </div>
      )}

      <p className="text-xs text-stone mb-3">Comparé aux {days} jours précédents · commandes payées (CB) et à facturer (30j)</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <BizCard icon={Euro} label="Chiffre d'affaires" value={eur(c.revenue)} delta={biz?.deltas?.revenue} color="text-forest" />
        <BizCard icon={Wallet} label="Coût d'achat" value={eur(c.cost)} color="text-terracotta" hint="marchandises" />
        <BizCard icon={PiggyBank} label="Marge brute" value={eur(c.margin)} delta={biz?.deltas?.margin} color="text-green-600" hint={`${c.margin_pct || 0}%`} />
        <BizCard icon={ShoppingBag} label="Commandes" value={c.orders || 0} delta={biz?.deltas?.orders} color="text-forest" hint={`panier moy. ${eur(c.avg_order)}`} />
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mb-10">
        <div className="rounded-xl bg-white border border-border p-6">
          <h3 className="font-semibold text-forest mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-brand" /> CA, coûts & marge</h3>
          {loadingBiz ? <p className="text-sm text-stone">Chargement…</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={biz?.daily || []}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2A5A3B" stopOpacity={0.3} /><stop offset="95%" stopColor="#2A5A3B" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gMar" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#16a34a" stopOpacity={0.25} /><stop offset="95%" stopColor="#16a34a" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#4A5D52" }} tickFormatter={(d) => d?.slice(5)} minTickGap={20} />
                <YAxis tick={{ fontSize: 11, fill: "#4A5D52" }} tickFormatter={(v) => `${v}€`} />
                <Tooltip formatter={(v, n) => [eur(v), n === "revenue" ? "CA" : n === "cost" ? "Coût" : "Marge"]} labelFormatter={(d) => d} />
                <Legend formatter={(v) => (v === "revenue" ? "CA" : v === "cost" ? "Coût" : "Marge")} />
                <Area type="monotone" dataKey="revenue" stroke="#2A5A3B" strokeWidth={2} fill="url(#gRev)" />
                <Area type="monotone" dataKey="margin" stroke="#16a34a" strokeWidth={2} fill="url(#gMar)" />
                <Area type="monotone" dataKey="cost" stroke="#A66E3F" strokeWidth={1.5} fillOpacity={0} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-xl bg-white border border-border p-6" data-testid="top-products">
          <h3 className="font-semibold text-forest mb-4">Meilleures ventes</h3>
          {(!biz?.top_products || biz.top_products.length === 0) ? <p className="text-sm text-stone">Aucune vente sur la période</p> : (
            <div className="space-y-3">
              {biz.top_products.slice(0, 6).map((p) => (
                <div key={p.product_id} className="flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-forest truncate font-medium">{p.name}</p>
                    <p className="text-xs text-stone">{p.qty} vendu(s) · marge {eur(p.margin)}</p>
                  </div>
                  <span className="font-semibold text-brand shrink-0">{eur(p.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============ TRAFFIC ============ */}
      <h2 className="font-display text-2xl text-forest mb-4 flex items-center gap-2"><Globe size={22} className="text-brand" /> Audience & référencement</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <Card icon={Eye} label="Visites totales" value={s.total_views} hint="Toutes pages" />
        <Card icon={UserCheck} label="Visiteurs uniques" value={s.unique_visitors} hint="Visiteurs distincts" />
        <Card icon={Users2} label="Leads" value={s.leads_total} hint={`${s.leads_new} nouveaux`} />
        <Card icon={FileText} label="Contenus publiés" value={s.content} hint={`${s.realisations} réalisations`} />
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 mb-6">
        <div className="rounded-xl bg-white border border-border p-6">
          <h3 className="font-semibold text-forest mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-brand" /> Trafic — 30 derniers jours</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={s.daily}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2A5A3B" stopOpacity={0.3} /><stop offset="95%" stopColor="#2A5A3B" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#4A5D52" }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#4A5D52" }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="views" stroke="#2A5A3B" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl bg-white border border-border p-6">
          <h3 className="font-semibold text-forest mb-4">Pages les plus visitées</h3>
          {s.top_pages.length === 0 ? <p className="text-sm text-stone">Aucune donnée</p> : (
            <div className="space-y-2">
              {s.top_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-forest truncate max-w-[70%]">{p.path}</span>
                  <span className="font-semibold text-brand">{p.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Sources */}
        <div className="rounded-xl bg-white border border-border p-6" data-testid="traffic-sources">
          <h3 className="font-semibold text-forest mb-4 flex items-center gap-2"><Globe size={18} className="text-brand" /> Sources de trafic</h3>
          {(!s.sources || s.sources.length === 0) ? <p className="text-sm text-stone">Aucune donnée</p> : (
            <div className="space-y-3">
              {s.sources.map((src) => (
                <div key={src.name}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-forest">{src.name}</span><span className="text-stone">{Math.round((src.count / totalSources) * 100)}%</span></div>
                  <div className="h-2 rounded-full bg-sage overflow-hidden"><div className="h-full bg-brand" style={{ width: `${(src.count / totalSources) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="rounded-xl bg-white border border-border p-6" data-testid="device-breakdown">
          <h3 className="font-semibold text-forest mb-4 flex items-center gap-2"><Monitor size={18} className="text-brand" /> Appareils</h3>
          {(!s.devices || s.devices.length === 0) ? <p className="text-sm text-stone">Aucune donnée</p> : (
            <div className="space-y-4 pt-2">
              {s.devices.map((d) => {
                const Icon = DEVICE_ICON[d.name] || Monitor;
                return (
                  <div key={d.name} className="flex items-center gap-3">
                    <Icon size={18} className="text-forest shrink-0" />
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1"><span className="text-forest">{DEVICE_LABEL[d.name] || d.name}</span><span className="text-stone">{Math.round((d.count / totalDevices) * 100)}%</span></div>
                      <div className="h-2 rounded-full bg-sage overflow-hidden"><div className="h-full bg-terracotta" style={{ width: `${(d.count / totalDevices) * 100}%` }} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Keyword summary */}
        <div className="rounded-xl bg-white border border-border p-6" data-testid="keyword-summary">
          <h3 className="font-semibold text-forest mb-4 flex items-center gap-2"><Search size={18} className="text-brand" /> Positionnement SEO</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-[#F0EFEA] p-4"><p className="font-display text-3xl text-forest">{s.keywords?.tracked ?? 0}</p><p className="text-xs text-stone">Mots-clés suivis</p></div>
            <div className="rounded-lg bg-[#F0EFEA] p-4"><p className="font-display text-3xl text-forest">{s.keywords?.avg_position ?? "—"}</p><p className="text-xs text-stone">Position moyenne</p></div>
            <div className="rounded-lg bg-[#F0EFEA] p-4"><p className="font-display text-3xl text-green-600">{s.keywords?.top3 ?? 0}</p><p className="text-xs text-stone">Top 3 Google</p></div>
            <div className="rounded-lg bg-[#F0EFEA] p-4"><p className="font-display text-3xl text-brand">{s.keywords?.top10 ?? 0}</p><p className="text-xs text-stone">Top 10 Google</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}
